import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import { useDispatch, useSelector } from 'react-redux';

import { uploadWipExcel } from '../../reducers/wip.reducer';

const WIPUploadModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const [vendor, setVendor] = useState('');
  const [season, setSeason] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Get upload state from Redux
  const uploading = useSelector(state => state.wip.uploading);
  const error = useSelector(state => state.wip.error);
  const uploadSuccess = useSelector(state => state.wip.uploadSuccess);

  // TODO: Fetch actual vendors/suppliers from backend
  const vendors = [
    '03727 -Shanghai Loyal Knit Co., Ltd',
    'Vendor ABC',
    'Guangzhou 1',
    'Vendor 1',
    'Vendor 2',
    'CHUN XUE (CAMBODIA) CASHMERE TEXTILE CO., LTD.',
    'VIETNAM TEXTILE MANUFACTURING CO., LTD.',
  ];

  const seasons = [
    'Q1, 2025',
    'Q2, 2025',
    'Q3, 2025',
    'Q4, 2025',
    'Q1, 2026',
    'Q2, 2026',
    'Q3, 2026',
    'Q4, 2026',
  ];

  // Set default season when modal opens
  useEffect(() => {
    if (open && !season) {
      const now = new Date();
      const year = now.getFullYear();
      const quarter = Math.floor(now.getMonth() / 3) + 1;
      setSeason(`Q${quarter}, ${year}`);
    }
  }, [open, season]);

  // Close modal on successful upload
  useEffect(() => {
    if (uploadSuccess) {
      handleClose();
    }
  }, [uploadSuccess]);

  const handleFileUpload = event => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleDragOver = event => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = event => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleImport = () => {
    if (!uploadedFile || !season) return;

    const formData = new FormData();
    formData.append('file', uploadedFile);
    formData.append('season', season);
    if (vendor) {
      formData.append('vendor', vendor);
    }

    dispatch(uploadWipExcel({ formData }));
  };

  const handleClose = () => {
    if (!uploading) {
      setVendor('');
      setSeason('');
      setUploadedFile(null);
      setIsDragging(false);
      onClose();
    }
  };

  const isFormValid = season && uploadedFile;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Upload New WIP Data
        </Typography>
        <IconButton onClick={handleClose} size="small" disabled={uploading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Error Message */}
          {error && (
            <Alert
              severity="error"
              onClose={() => {}}
              sx={{ borderRadius: '8px' }}
            >
              {error}
            </Alert>
          )}

          {/* Vendor Dropdown (Optional) */}
          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500, color: '#424242' }}
            >
              Vendor/Supplier (Optional)
            </Typography>
            <TextField
              select
              fullWidth
              value={vendor}
              onChange={e => setVendor(e.target.value)}
              placeholder="Select vendor"
              size="small"
              disabled={uploading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            >
              <MenuItem value="">
                <em>None - Auto-detect from file</em>
              </MenuItem>
              {vendors.map(v => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Season Dropdown */}
          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500, color: '#424242' }}
            >
              Season *
            </Typography>
            <TextField
              select
              fullWidth
              value={season}
              onChange={e => setSeason(e.target.value)}
              placeholder="Select season"
              size="small"
              disabled={uploading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            >
              {seasons.map(s => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* File Upload Dropzone */}
          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500, color: '#424242' }}
            >
              Excel File *
            </Typography>
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: isDragging
                  ? '2px dashed #4B68D4'
                  : '2px dashed #BDBDBD',
                borderRadius: '12px',
                padding: 4,
                textAlign: 'center',
                backgroundColor: isDragging ? '#E3F2FD' : '#F9F9F9',
                cursor: uploading ? 'not-allowed' : 'pointer',
                opacity: uploading ? 0.6 : 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: uploading ? '#F9F9F9' : '#F0F0F0',
                  borderColor: uploading ? '#BDBDBD' : '#4B68D4',
                },
              }}
              onClick={() => {
                if (!uploading) {
                  document.getElementById('file-upload-input').click();
                }
              }}
            >
              <input
                id="file-upload-input"
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                disabled={uploading}
              />

              {uploadedFile ? (
                <Box>
                  <DescriptionIcon
                    sx={{ fontSize: 48, color: '#4B68D4', mb: 1 }}
                  />
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {uploadedFile.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {(uploadedFile.size / 1024).toFixed(2)} KB
                  </Typography>
                  {!uploading && (
                    <Button
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        setUploadedFile(null);
                      }}
                      sx={{ mt: 1, textTransform: 'none' }}
                    >
                      Remove file
                    </Button>
                  )}
                </Box>
              ) : (
                <Box>
                  <CloudUploadIcon
                    sx={{ fontSize: 48, color: '#BDBDBD', mb: 1 }}
                  />
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Drag & drop your Excel file here
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    or click to browse
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Supported formats: .xlsx, .xls, .csv
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Helper Alert */}
          <Alert severity="info" sx={{ borderRadius: '8px' }}>
            <Typography variant="body2">
              Upload an Excel file containing WIP tracker data with all required
              columns (Vendor Style #, PO #, dates, etc.)
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={uploading}
          sx={{
            textTransform: 'none',
            color: '#616161',
            '&:hover': {
              backgroundColor: '#F5F5F5',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!isFormValid || uploading}
          startIcon={<CloudUploadIcon />}
          sx={{
            backgroundColor: '#4B68D4',
            color: 'white',
            textTransform: 'none',
            px: 3,
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: '#3A54B6',
            },
            '&:disabled': {
              backgroundColor: '#E0E0E0',
            },
          }}
        >
          {uploading ? 'Uploading...' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WIPUploadModal;

